import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocale } from '../context/LocaleContext';
import SafeIcon from '../common/SafeIcon';

const SPONSOR_TIERS = {
  monthly: [
    { id: 'm5', price: 5, nameKey: 'sponsor.tiers.m5.name', descKey: 'sponsor.tiers.m5.desc', link: 'https://buy.stripe.com/cNiaEYg1l3KB5LxcKS0Fi18' },
    { id: 'm8', price: 8, nameKey: 'sponsor.tiers.m8.name', descKey: 'sponsor.tiers.m8.desc', link: 'https://buy.stripe.com/dRm6oI9CXgxn7TFaCK0Fi19' },
    { id: 'm10', price: 10, nameKey: 'sponsor.tiers.m10.name', descKey: 'sponsor.tiers.m10.desc', link: 'https://buy.stripe.com/6oUbJ2cP9bd3c9VbGO0Fi1a' },
  ],
  onetime: [
    { id: 'o5', price: 5, nameKey: 'sponsor.tiers.o5.name', descKey: 'sponsor.tiers.o5.desc', link: 'https://buy.stripe.com/7sY9AUg1l0ypc9VeT00Fi1b' },
    { id: 'o8', price: 8, nameKey: 'sponsor.tiers.o8.name', descKey: 'sponsor.tiers.o8.desc', link: 'https://buy.stripe.com/3cIcN6cP9bd34HtfX40Fi1c' },
    { id: 'o10', price: 10, nameKey: 'sponsor.tiers.o10.name', descKey: 'sponsor.tiers.o10.desc', link: 'https://buy.stripe.com/dRm5kE02nftj6PB12a0Fi1d' },
  ]
};

export default function SponsorModal({ isOpen, onClose }) {
  const { t } = useLocale();
  const [paymentType, setPaymentType] = useState('monthly');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setTimeout(() => setPaymentType('monthly'), 300);
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const currentTiers = SPONSOR_TIERS[paymentType];

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
            onClick={onClose}
          />
          
          <motion.div
            initial={{ y: "100%", opacity: 0, scale: 0.95 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: "100%", opacity: 0, scale: 0.95 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-[#fffbf0] dark:bg-slate-900 w-full sm:w-[480px] rounded-t-[2rem] sm:rounded-[2.5rem] shadow-2xl relative z-10 flex flex-col overflow-hidden border-4 border-white dark:border-slate-800"
            style={{ maxHeight: '90vh' }}
          >
            <div className="bg-gradient-to-r from-pink-100 to-rose-200 dark:from-pink-900/40 dark:to-rose-900/40 px-6 py-5 flex justify-between items-center shrink-0">
              <h2 className="text-xl font-black text-rose-700 dark:text-pink-300 flex items-center gap-2">
                <SafeIcon name="Heart" className="w-6 h-6 animate-pulse" />
                {t('sponsor.title')}
              </h2>
              <button onClick={onClose} className="p-2 bg-white/50 dark:bg-black/20 hover:bg-white dark:hover:bg-black/40 rounded-full text-rose-700 dark:text-pink-300 transition-colors">
                <SafeIcon name="X" className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <div className="text-center mb-6">
                <p className="text-gray-600 dark:text-gray-300 font-medium leading-relaxed">
                  {t('sponsor.desc1')}<strong>DaysMatter</strong>{t('sponsor.desc2')}<br className="hidden sm:block" />
                  {t('sponsor.desc3')}
                </p>
              </div>

              <div className="bg-white dark:bg-slate-800 p-1.5 rounded-2xl flex gap-1 mb-6 shadow-sm border border-gray-100 dark:border-slate-700 relative">
                <button onClick={() => setPaymentType('monthly')} className={`flex-1 py-3 rounded-xl font-bold text-sm transition-all z-10 ${paymentType === 'monthly' ? 'text-gray-800 dark:text-white' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'}`}>
                  {t('sponsor.monthly')}
                </button>
                <button onClick={() => setPaymentType('onetime')} className={`flex-1 py-3 rounded-xl font-bold text-sm transition-all z-10 ${paymentType === 'onetime' ? 'text-gray-800 dark:text-white' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'}`}>
                  {t('sponsor.onetime')}
                </button>
                <div className="absolute top-1.5 bottom-1.5 w-[calc(50%-0.375rem)] bg-pink-100 dark:bg-slate-700 rounded-xl shadow-sm transition-transform duration-300 ease-out border border-pink-200 dark:border-slate-600" style={{ transform: paymentType === 'monthly' ? 'translateX(0)' : 'translateX(calc(100% + 0.25rem))' }} />
              </div>

              <div className="space-y-4">
                {currentTiers.map((tier) => (
                  <div key={tier.id} className="bg-white dark:bg-slate-800 border-2 border-gray-100 dark:border-slate-700 rounded-2xl p-4 flex items-center gap-4 hover:border-pink-300 dark:hover:border-pink-500/50 transition-all hover:shadow-md group">
                    <div className="w-14 h-14 shrink-0 bg-pink-50 dark:bg-slate-700 rounded-xl flex items-center justify-center text-rose-500 dark:text-pink-400 font-black text-xl">
                      ${tier.price}
                    </div>
                    <div className="flex-1 min-w-0 py-1">
                      <h3 className="font-bold text-gray-800 dark:text-white text-lg break-words leading-tight">
                        {t(tier.nameKey)}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 break-words leading-relaxed">
                        {t(tier.descKey)}
                      </p>
                    </div>
                    <a 
                      href={tier.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0 bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-4 py-2.5 rounded-xl font-bold text-sm hover:bg-pink-500 dark:hover:bg-pink-400 hover:text-white transition-all shadow-sm group-hover:scale-105 active:scale-95"
                    >
                      {t('sponsor.btn')}
                    </a>
                  </div>
                ))}
              </div>

              <div className="mt-6 text-center">
                <p className="text-xs font-bold text-gray-400 dark:text-gray-500 flex items-center justify-center gap-1">
                  <SafeIcon name="Lock" className="w-3 h-3" />
                  {t('sponsor.secure')}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}